import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import EditableText from '@sayyesschool/frontend/components/editable-text';
import ImageSection from '@sayyesschool/frontend/components/image-section';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { getImagePath, useCourse } from '@sayyesschool/frontend/features/courses';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Flex, Grid, Heading } from '@sayyesschool/frontend/ui-components';

import CourseDescription from '../course-description';
import CourseUnits from '../course-units';

export default function CoursePage() {
    const navigate = useNavigate();
    const params = useParams();

    const [course, actions] = useCourse(params.courseId);

    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const courseId = course?.id;

    const handleUpdateCourse = useCallback(data => {
        return actions.updateCourse(courseId, data);
    }, [courseId, actions]);

    const handleUpdateCourseTitle = useCallback(title => {
        return actions.updateCourse(courseId, { title });
    }, [courseId, actions]);

    const handleUpdateCourseImage = useCallback(image => {
        return actions.updateCourse(courseId, { image });
    }, [courseId, actions]);

    const handleDeleteCourse = useCallback(() => {
        return actions.deleteCourse(courseId)
            .then(() => navigate('/courses'));
    }, [courseId, actions, navigate]);

    const handleCreateUnit = useCallback(data => {
        return actions.createUnit(courseId, data);
    }, [courseId, actions]);

    const handleDeleteUnit = useCallback(unit => {
        return actions.deleteUnit(courseId, unit.id);
    }, [courseId, actions]);

    if (!course) return <LoadingIndicator fullscreen />;

    return (
        <Page className="CoursePage">
            <Page.Header
                title={
                    <EditableText
                        as={Heading}
                        type="h2"
                        content={course.title}
                        required
                        onUpdate={handleUpdateCourseTitle}
                    />
                }
                actions={[
                    {
                        key: 'delete',
                        icon: 'delete',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid spacing={2}>
                    <Grid.Item xs={8}>
                        <CourseDescription
                            course={course}
                            onUpdate={handleUpdateCourse}
                        />
                    </Grid.Item>

                    <Grid.Item xs={4}>
                        <Flex
                            spacing={2}
                            column
                        >
                            <ImageSection
                                image={course.image}
                                path={getImagePath(course.id)}
                                onChange={handleUpdateCourseImage}
                            />

                            <CourseUnits
                                course={course}
                                onCreate={handleCreateUnit}
                                onDelete={handleDeleteUnit}
                            />
                        </Flex>
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <ConfirmationDialog
                title="Удалить курс?"
                message={`Курс "${course.title}" будет удален без возможности восстановления.`}
                open={isConfirmationDialogOpen}
                onConfirm={handleDeleteCourse}
                onClose={toggleConfirmationDialogOpen}
            />
        </Page>
    );
}